import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Justify, Select } from 'tea-component';
import { omit, isEmpty, uniq } from 'lodash-es';
import { useIntl } from 'umi';
import { useMemoizedFn, useSetState } from 'ahooks';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Tables from '../Tables';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import { TOP_COMPANIES_NUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import {
  formattingKolSearchList,
  formattingGameSearchList,
  handleTableDefaultSelectGames,
} from '../../../utils';
import { useStreamingKolRankList, useStreamingKolSearch } from '../../../hooks';
import { currentMonth, lastMonth } from '../..';
import defaultKol from '@/pages/intelligence/assets/svg/tipicon/kol-default.svg';

// import Tables from '@/pages/intelligence/pages/TopCharts/Companies/components/CompaniesCore/components/Tables';
interface StreamingKolTableListProps {
  paramsDefault: { [key in string]: unknown };
  userIds: { [key in string]: unknown };
  setUserIds: (...args: any[]) => void;
  setParamsDefault: (val: boolean) => void;
  setTableLoading: (...args: any[]) => void;
  downloadLogConfigKey: string;
  [key: string]: unknown;
}

const StreamingKolTableList: FunctionComponent<StreamingKolTableListProps> = ({
  paramsDefault,
  setUserIds,
  userIds,
  intableRef,
  downloadLogConfigKey,
  setParamsDefault,
  setTableLoading
}) => {
  const [changeValue, setChangeValue] = useState<string>('kol_game');
  // 表格参数
  const [kolRankListProps, setKolRankListProps] = useSetState<{
    [key in string]: unknown;
  }>({
    order_metric: 'hoursWatched',
    order: 'desc',
  });
  // 缓存上一次的全选默认值
  const tableSelectionAllRefs = useRef<any>([]);
  // 缓存第一次的全选默认值
  const tableSelectionAllOneRefs = useRef<any>([]);
  // 存储多选checkStatus状态标识
  const operatedGame = useRef<string>('');
  // 是否需要更新图表数据
  const ecahrtsIsRefresh = useRef<boolean>(false);
  // 表格数据
  const {
    data,
    loading: tableLoading,
    error,
    mutate,
  } = useStreamingKolRankList(
    useMemo(
      () => ({ ...paramsDefault, ...kolRankListProps }),
      [kolRankListProps, paramsDefault],
    ),
  );
  // 表格搜索接口数据
  const { runAsync } = useStreamingKolSearch(changeValue);
  // 存储最新的动态勾选值
  const [tableSelectionNews, setTableSelection] = useState([]);
  // 中英切换方法
  const { formatMessage } = useIntl();
  const [imgActive, setImgActive] = useState('');
  const { ratio_type, order } = paramsDefault;
  const dataTable = data?.data;
  const options = [
    { value: 'kol_game', text: formatMessage({ id: 'dbi_games' }) },
    { value: 'kol_anchor', text: formatMessage({ id: 'dbi_Primary_kol' }) },
  ];
  // 当未查询到数据时，或者其他异常情况，清空,如果报错，将会重新请求3次，可以使用上一次的请求状态，缓存起来，进行回滚，重新拉取最新的数据，目前先设置为空数组把
  useEffect(() => {
    if (error) {
      // domesomething
      mutate([]);
    }
    if (tableLoading) {
      mutate([]);
    }
  }, [error, mutate, tableLoading]);
  useImperativeHandle(intableRef, () => ({
    loading: tableLoading,
    ecahrtsIsRefresh: () => {
      handleClearSearch();
    },
    dateTimes: [data?.req?.start_date, data?.req?.end_date],
    sortTextKeys:
      kolRankListProps?.order_metric +
      (kolRankListProps?.ratio_type === 1 ? '_1' : ''),
  }));
  // 初始化获取userIds和缓存第一次数据
  useEffect(() => {
    if (kolRankListProps?.page >= 1) return;
    hanleOnSliceArrUserIds();
    tableSelectionAllOneRefs.current = handleTableDefaultSelectGames(
      dataTable?.list?.slice(0, TOP_COMPANIES_NUM),
      kolRankListProps.page,
    );
  }, [kolRankListProps?.page, dataTable?.list]);

  useEffect(()=> {
    setTableLoading(tableLoading)
  }, [tableLoading])

  // 更新userIds规则
  useEffect(() => {
    // 当排序字段改变的时候, 或者允许更新图表才会report
    if (
      kolRankListProps.order_metric !== 'hoursWatched' ||
      kolRankListProps.order !== 'desc' ||
      ecahrtsIsRefresh.current
    ) {
      hanleOnSliceArrUserIds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTable?.list, kolRankListProps.hoursWatched, kolRankListProps.order]);

  // 获取userIds,
  const hanleOnSliceArrUserIds = useCallback(() => {
    const res = dataTable?.list
      ?.slice(0, userIds.addNumber)
      .map((task) => task.user_info.id);
    setUserIds((pre) => ({ ...pre, userList: res }));
  }, [dataTable?.list, setUserIds, userIds.addNumber]);

  // 根据勾选的条数动态增加，规则集中在这块
  const tableSelection = useMemo(() => {
    // 取消全选标识为cancel，说明为空，给[]
    if (operatedGame.current === 'cancel') {
      return [];
    }
    // 传入的状态值有值且为增删，用当前点击事件传入的selectList
    if (
      !isEmpty(operatedGame.current) &&
      ['add', 'del'].includes(operatedGame.current)
    ) {
      return tableSelectionNews;
    }
    // 当进行搜索下拉框时，用默认的,如果在搜索下拉框前，有勾选其他，用最新勾选的
    if (kolRankListProps?.user_ids || kolRankListProps?.search_game_id) {
      if (!isEmpty(tableSelectionNews)) {
        return tableSelectionNews;
      }
      return tableSelectionAllOneRefs.current;
    }
    // 当进行分页时，用默认的,如果在分页前，有勾选其他，用最新勾选的
    if (kolRankListProps?.page > 1) {
      if (!isEmpty(tableSelectionNews)) {
        return tableSelectionNews;
      }
      return tableSelectionAllOneRefs.current;
    }
    // 默认渲染初始值
    const defauletList = handleTableDefaultSelectGames(
      dataTable?.list?.slice(0, TOP_COMPANIES_NUM),
      kolRankListProps?.page,
    );
    return defauletList;
  }, [tableSelectionNews, kolRankListProps, dataTable?.list]);

  // 默认6条
  const tableSelectionAll = useMemo(() => {
    if (kolRankListProps?.page > 1) {
      return tableSelectionAllRefs.current;
    }
    tableSelectionAllRefs.current = handleTableDefaultSelectGames(
      dataTable?.list?.slice(0, TOP_COMPANIES_NUM),
    );
    return handleTableDefaultSelectGames(
      dataTable?.list?.slice(0, TOP_COMPANIES_NUM),
    );
  }, [dataTable?.list, kolRankListProps.page]);

  // TODO: 表格事件以及表格数据封装hooks，用在kol以及gameS页面
  // 表格排序事件
  const onTableSortChage = (e) => {
    if (isEmpty(e)) return;
    const { ratioType, sortMetric, sortVal } = e || {};
    ecahrtsIsRefresh.current = true;
    setParamsDefault({
      ...kolRankListProps,
      order_metric: sortMetric,
      order: sortVal,
    });
    setKolRankListProps({
      ...kolRankListProps,
      order_metric: sortMetric,
      ratio_type: ratioType ?? 0,
      order: sortVal,
      page: 1,
      page_size: 100,
    });
  };
  // 表格分页事件
  const onTablePagingChange = (e) => {
    const { pageIndex, pageSize } = e || {};
    ecahrtsIsRefresh.current = false;
    setKolRankListProps({
      ...kolRankListProps,
      page: pageIndex,
      page_size: pageSize,
    });
    if (pageIndex === 1 && isEmpty(tableSelectionNews)) {
      operatedGame.current = 'add';
      setTableSelection(tableSelectionAllOneRefs.current);
      ecahrtsIsRefresh.current = true;
    }
  };
  // 表格check多选事件
  const onTableCheckGame = <T extends unknown>(
    selectList: T[],
    checkStatus: string,
  ) => {
    operatedGame.current = checkStatus;
    const userList = selectList.map((item) => item?.user_info?.id) || [];
    const mergeList = uniq(userList);
    setUserIds((pre) => ({ ...pre, userList: mergeList }));
    setTableSelection(selectList);
  };
  // 清空回调, 发现其他榜单规则和这个不一样,目前做的是多选、图表还原
  const handleClearSearch = useMemoizedFn(() => {
    // 清空搜索值
    setKolRankListProps((pre: any) => {
      // 没找到更好的方法，直接修改入参把，当浅拷贝的时候，监听不到值的变化
      delete pre?.search_game_id;
      delete pre?.user_ids;
      return {
        ...pre,
        page: 1,
        page_size: 100,
      };
    });
    // 表格还原
    operatedGame.current = '';
    // 图表还原
    ecahrtsIsRefresh.current = true;
    setTableSelection([]);
    // 高亮图片
    setImgActive('');
  });
  // 搜索结果格式化回调
  const transTableDataForSearch = useMemoizedFn((res: any) => {
    const resultList = res;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    return changeValue === 'kol_game'
      ? formattingGameSearchList(resultList)
      : formattingKolSearchList(resultList);
  });
  // 下拉选择框搜索点击
  const getTableSearchList = useMemoizedFn(
    (id: string, values: { [key in string]: string }) => {
      ecahrtsIsRefresh.current = false;
      setImgActive(values.imgSrc);
      setKolRankListProps((pre: any) => {
        delete pre?.user_ids;
        delete pre?.search_game_id;
        if (changeValue === 'kol_game') {
          pre.search_game_id = id;
        } else if (changeValue === 'kol_anchor') {
          pre.user_ids = [id];
        }
        return {
          ...pre,
          page: 1,
          page_size: 100,
        };
      });
    },
  );
  // 搜索回调
  const getMetricsTableDataRankList = useMemoizedFn(
    ({ search_by_name }: { search_by_name: string }) => {
      const timeDate = {
        end_date: currentMonth,
        start_date: lastMonth,
      };
      ecahrtsIsRefresh.current = false;
      const params = {
        ...paramsDefault,
        ...timeDate,
        ...omit(kolRankListProps, ['user_ids']),
        search_by_name,
        search_name: search_by_name,
      };
      // 当进行搜索时，是不允许携带search_game_id
      return runAsync(params).then((res) => res);
    },
  );
  const selectedIds = useMemo(() => tableSelection?.map((item: any) => item?.user_info?.id) || [], [tableSelection]);

  const getCheckedLinesCfg = () => ({ key: 'select_ids', value: selectedIds });  

  return (
    <>
      <Justify
        className="dbi-pcGame-jf pc-metricstable-jf dbi-card-justify"
        right={
          <>
            <Select
              className="kol-details-select"
              appearance="button"
              options={options}
              value={changeValue}
              onChange={(value) => setChangeValue(value)}
            />
            <SearchRecent
              resetSearchType={changeValue}
              placeholder={formatMessage(
                { id: 'dbi_table_search_placeholder' },
                {
                  domainName: formatMessage({
                    id:
                      changeValue === 'kol_game'
                        ? 'dbi_game'
                        : 'dbi_Primary_kol',
                  }),
                },
              )}
              fetchParams={{ ...paramsDefault, ...kolRankListProps }}
              handleClear={handleClearSearch}
              initSearchInput={true}
              // 表格id
              getTableResultList={getTableSearchList}
              // 搜索接口
              searchListApi={getMetricsTableDataRankList}
              // 格式化
              transTableDataForSearch={transTableDataForSearch}
              defaultImg={changeValue === 'kol_anchor' ? defaultKol : null}
            />
            <ExportTable
              classModalName=""
              entityType="streaming"
              // @ts-ignore
              businessType="streaming-anchors-more"
              downloadModalSize={820}
              isDisableDownload={tableLoading}
              downloadLogConfigKey={downloadLogConfigKey}
              // excelName=""
              total={(dataTable as any)?.total}
              isNeedCheckedLines={true}
              tableParams={{
                ...paramsDefault,
                ...kolRankListProps,
                select_ids: selectedIds,
                start_date: data?.req?.start_date || '',
                end_date: data?.req?.end_date || '',
              }}
              getCheckedLinesCfg={getCheckedLinesCfg}
            />
          </>
        }
      />
      <div id="metrics-table-container">
        <CardSlot>
          <Tables
            {...{
              topChartsType: 'streaming_kol_revenue',
              records: dataTable?.list,
              loading: tableLoading,
              tableTotal: dataTable?.total,
              imgActive,
              tableSelection,
              tableSelectionAll,
              pageSortAttr: {
                currentOrderSource: '',
                currentSortMetric: kolRankListProps.order_metric,
                currentSortValue: kolRankListProps?.order,
                currentRatioType: kolRankListProps?.ratio_type || 0,
                tablePageIndex: kolRankListProps?.page,
                tablePageSize: kolRankListProps?.page_size || 100,
              },
              gameTypes: ['hoursWatched', 'peackCCV', 'avgCCV', 'airtime'],
              onTableSortChage,
              onTablePagingChange,
              onTableCheckGame,
            }}
          />
        </CardSlot>
      </div>
    </>
  );
};

export default memo(StreamingKolTableList);
